import "prismjs";
import "prismjs/components/prism-bash.js";
import "prismjs/components/prism-diff.js";
import "prismjs/plugins/toolbar/prism-toolbar.js";
import "prismjs/plugins/copy-to-clipboard/prism-copy-to-clipboard.js";

import {
  sectionNames,
  getMenuStructure,
  findSectionByName,
  getSectionNameFromDataId,
  fetchSectionContent,
} from "./model.js";
import BaseView from "./views/baseView.js";
import TopMenuView from "./views/topMenuView.js";
import SectionView from "./views/sectionView.js";
import LeftMenuView from "./views/leftMenuView.js";
import { capitalizeFirstLetter } from "./helper.js";

const DEFAULT_SECTION_NAME = sectionNames[0];
const HOME_SECTION_NAME = "home";
const SAVED_THEME_STORAGE_KEY = "selectedTheme";

let activeSectionName = null;
let renderedSectionNames = [];

const resolveSectionName = (requestedSectionName) => {
  const isKnownSection = sectionNames.includes(requestedSectionName);
  if (isKnownSection) {
    return requestedSectionName;
  }
  return DEFAULT_SECTION_NAME;
};

const readSectionNameFromUrl = () => {
  const pathSegments = window.location.pathname
    .split("/")
    .filter((segment) => segment.length > 0);
  const requestedSectionName = (pathSegments[0] ?? "").toLowerCase();
  return resolveSectionName(requestedSectionName);
};

const handleObserverIntersect = (dataId) => {
  LeftMenuView.setActiveLink(dataId);
  LeftMenuView.scrollToLink(dataId);
};

const renderSectionContent = (sectionName) => {
  const isAlreadyRendered = renderedSectionNames.includes(sectionName);
  if (isAlreadyRendered) {
    SectionView.showSection(sectionName);
  } else {
    const content = fetchSectionContent(sectionName);
    SectionView.renderContent(sectionName, content);
    renderedSectionNames.push(sectionName);

    if (sectionName === HOME_SECTION_NAME) {
      SectionView.renderGithubCalendar();
    }
  }

  SectionView.cleanup();
  SectionView.addIntersectionObserver(handleObserverIntersect, sectionName);
};

const renderLeftMenu = (sectionName) => {
  const section = findSectionByName(sectionName);
  const hasSubmenu = section.children.length > 0;

  if (hasSubmenu) {
    const leftMenuHTML = LeftMenuView.generateLeftMenuHTML(section);
    LeftMenuView.render(leftMenuHTML);
  }
  LeftMenuView.toggleLeftMenu(hasSubmenu);
};

const activateSection = (sectionName) => {
  activeSectionName = sectionName;

  BaseView.setBrowserTabTitle(
    `Autumn8 - ${capitalizeFirstLetter(sectionName)}`
  );
  TopMenuView.setActiveLink(sectionName);
  renderSectionContent(sectionName);
  SectionView.setActiveSection(sectionName);
  renderLeftMenu(sectionName);
};

const handleTopMenuClick = (dataId) => {
  const sectionName = resolveSectionName(getSectionNameFromDataId(dataId));
  const isAlreadyActive = sectionName === activeSectionName;

  if (isAlreadyActive) {
    SectionView.scrollTo(dataId);
    return;
  }

  activateSection(sectionName);
  window.history.pushState({ section: sectionName }, "", `/${sectionName}`);
  SectionView.scrollTo(dataId);
};

const handleLeftMenuClick = (dataId) => {
  SectionView.scrollTo(dataId);
};

const init = () => {
  TopMenuView.initHamburgerMenu();
  TopMenuView.initThemeSelection();
  TopMenuView.render(TopMenuView.generateTopMenuHTML(getMenuStructure()));
  TopMenuView.addHandlerClick(handleTopMenuClick);
  LeftMenuView.addHandlerClick(handleLeftMenuClick);

  SectionView.render(SectionView.generateSectionsHTML(sectionNames));

  const sectionName = readSectionNameFromUrl();
  window.history.replaceState({ section: sectionName }, "", `/${sectionName}`);
  activateSection(sectionName);

  const savedThemeName = localStorage.getItem(SAVED_THEME_STORAGE_KEY);
  TopMenuView.setTheme(savedThemeName);
};

if (module.hot) {
  module.hot.accept(() => {
    renderedSectionNames = [];
    activateSection(readSectionNameFromUrl());
    Prism.highlightAll();
  });
}

document.addEventListener("DOMContentLoaded", init);

window.addEventListener("load", () => {
  document.body.classList.add("is-loaded");
});

window.addEventListener("popstate", (event) => {
  document.body.classList.add("is-loaded");

  let requestedSectionName = DEFAULT_SECTION_NAME;
  if (event.state && event.state.section) {
    requestedSectionName = event.state.section;
  }

  SectionView.hideAllSections();
  activateSection(resolveSectionName(requestedSectionName));
});
